import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box } from "@mui/material";
import LoadingSpinner from "components/basic/loadingSpinner";
import { TicketStatus } from "generated/graphql";
import { useSnack } from "hooks/useSnack";
import Composer from "pages/dashboard/pages/message/components/ConversationThread/components/Composer";

import CommentList from "./components/CommentList";
import TicketHeader from "./components/TicketHeader";
import { useTicket, useTicketActions } from "../../supportApi";

interface TicketDetailProps {
  ticketId: number;
  // Shows the support team's controls: any status, deleting comments/tickets.
  asAdmin?: boolean;
  onBack: () => void;
}

/** One ticket's thread and composer; the state owner for its actions. */
const TicketDetail: React.FC<TicketDetailProps> = ({
  ticketId,
  asAdmin = false,
  onBack,
}) => {
  const showSnack = useSnack();
  const { t } = useTranslation("support");
  const { ticket, comments, loading, refresh } = useTicket(ticketId, asAdmin);
  const actions = useTicketActions(asAdmin);
  const { markRead } = actions;
  const [sending, setSending] = useState(false);

  const unreadCount = ticket?.unreadCount ?? 0;
  useEffect(() => {
    if (unreadCount > 0 && document.hasFocus()) {
      markRead(ticketId);
    }
  }, [ticketId, unreadCount, markRead]);

  if (!ticket) {
    return loading ? (
      <LoadingSpinner />
    ) : (
      <Box p={4} textAlign="center">
        {t("detail.missing")}
      </Box>
    );
  }

  const run = async (
    action: () => Promise<{ ok: boolean; error?: string }>,
    fallback: string,
  ): Promise<boolean> => {
    const result = await action();
    if (!result.ok) {
      showSnack(result.error ?? fallback, "error");
    }
    return result.ok;
  };

  const handleSend = async (body: string, files: File[]) => {
    setSending(true);
    const ok = await run(
      () => actions.comment(ticketId, body, files),
      t("snack.comment_failed"),
    );
    setSending(false);
    if (ok) {
      refresh();
    }
    return ok;
  };

  const handleStatus = (status: TicketStatus) =>
    run(() => actions.setStatus(ticketId, status), t("snack.update_failed"));

  const handleDeleteTicket = async () => {
    if (
      await run(() => actions.deleteTicket(ticketId), t("snack.delete_failed"))
    ) {
      showSnack(t("snack.ticket_deleted"), "success");
      onBack();
    }
  };

  const handleDeleteComment = async (commentId: number) => {
    if (
      await run(
        () => actions.deleteComment(commentId),
        t("snack.delete_failed"),
      )
    ) {
      refresh();
    }
  };

  return (
    <Box display="flex" flexDirection="column" height="100%" minWidth={0}>
      <TicketHeader
        ticket={ticket}
        asAdmin={asAdmin}
        onBack={onBack}
        onStatusChange={handleStatus}
        onDelete={asAdmin ? handleDeleteTicket : undefined}
      />
      <CommentList
        comments={comments}
        asAdmin={asAdmin}
        onDelete={asAdmin ? handleDeleteComment : undefined}
      />
      <Composer sending={sending} onSend={handleSend} />
    </Box>
  );
};

export default TicketDetail;
